import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import BrowserFrame from './BrowserFrame';
import dashboardImage from '../../assets/erica/dashboard.png';
import { COPY } from '../../i18n/copy';
import { useI18n } from '../../i18n/useI18n';

const EricaHero: React.FC = () => {
  const { lang } = useI18n();
  const c = COPY[lang];
  const heroHighlights =
    lang === 'id'
      ? ['Hubungkan → Tanya → Insight!', 'Apa pun datanya, tinggal tanya.', 'Data + Tanya = Beres!']
      : ['Connect → Ask → Insight!', 'Any data, just ask.', 'Data + Questions = Decisions.'];

  const onRequestDemo = () => {
    const section = document.getElementById('demo');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      const first = document.querySelector<HTMLInputElement>('#demo input[name="fullName"]');
      first?.focus();
    }, 200);
  };

  return (
    <section
      id="top"
      className="relative overflow-hidden bg-gradient-to-b from-cyan-50/60 via-white to-white pb-14 pt-24 sm:pt-28"
    >
      <div className="pointer-events-none absolute -top-20 left-1/2 -z-10 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-300/30 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-16 -z-10 h-72 w-72 rounded-full bg-blue-300/20 blur-3xl" />
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white/80 px-3 py-1 text-sm text-cyan-800 shadow-sm">
            <Sparkles className="h-4 w-4" />
            <span className="bg-gradient-to-r from-cyan-700 to-blue-700 bg-clip-text font-semibold text-transparent">
              {c.hero.badge}
            </span>
          </div>

          <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
            {c.hero.headline}
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-slate-600">
            {c.hero.subhead}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
            <button
              type="button"
              onClick={onRequestDemo}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
            >
              {c.hero.primaryCta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <a
              href="#how-it-works"
              className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              {c.hero.secondaryCta}
            </a>
          </div>
        </div>

        <div className="relative mx-auto mt-12 max-w-6xl">
          <div className="pointer-events-none absolute -inset-8 -z-10 rounded-[40px] bg-gradient-to-br from-cyan-200/70 via-blue-100/40 to-white blur-2xl" />
          <div className="pointer-events-none absolute -left-8 bottom-8 -z-10 h-36 w-36 rounded-full bg-cyan-300/40 blur-2xl" />
          <div className="pointer-events-none absolute -right-8 top-8 -z-10 h-40 w-40 rounded-full bg-blue-300/30 blur-2xl" />
          <BrowserFrame
            src={dashboardImage}
            alt={c.hero.screenshotAlt}
            loading="eager"
            imageClassName="h-auto w-full bg-white object-cover"
          />
        </div>
      </div>
    </section>
  );
};

export default EricaHero;
